import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getReportTags } from '@/lib/api/report-tags';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pickSuggestionPlacement, SUGGESTION_LIST_MAX_HEIGHT, visibleBounds, } from './suggestionPlacement';
/**
 * The clinical-tag chip box shared by the report editor's Diagnoses field and
 * the worklist's study tags: the chips (click to cycle present -> suspected ->
 * ruled_out, X to remove) and, unless read-only, an input whose autocomplete
 * merges the scope's org tags with starter-vocabulary suggestions and offers a
 * "Create" row for free text. It holds no tag state of its own: every edit is
 * a callback, and the owner decides when to save.
 */
export function ClinicalTagChipBox({ chips, scope, readOnly, onCycle, onRemove, onAddOption, onAddFreeText, testIdPrefix = 'diagnoses', }) {
    const { t } = useTranslation('report');
    const linkedLabels = useMemo(() => new Set(chips.map((c) => c.label.toLowerCase())), [chips]);
    const removable = chips.filter((c) => !c.locked);
    // ─── Autocomplete ───
    const [inputValue, setInputValue] = useState('');
    const [inputFocused, setInputFocused] = useState(false);
    const [debouncedTerm, setDebouncedTerm] = useState('');
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedTerm(inputValue.trim()), 200);
        return () => clearTimeout(handle);
    }, [inputValue]);
    const suggestQuery = useQuery({
        queryKey: ['report-tag-suggest', scope, debouncedTerm],
        queryFn: () => getReportTags(scope, debouncedTerm),
        enabled: !readOnly && !!scope && debouncedTerm.length > 0,
        staleTime: 30_000,
    });
    const suggestions = useMemo(() => {
        const options = (suggestQuery.data ?? []).filter((o) => !linkedLabels.has(o.label.toLowerCase()));
        // Org tags first, vocabulary after — both capped to keep the popover short.
        return [
            ...options.filter((o) => o.source !== 'vocabulary'),
            ...options.filter((o) => o.source === 'vocabulary'),
        ].slice(0, 8);
    }, [suggestQuery.data, linkedLabels]);
    const trimmedInput = inputValue.trim();
    const hasExactMatch = suggestions.some((o) => o.label.toLowerCase() === trimmedInput.toLowerCase());
    const showCreateRow = trimmedInput.length > 0 && !hasExactMatch;
    const popoverOpen = inputFocused && trimmedInput.length > 0;
    // Open the list upward when it would be cut off below (the field usually
    // sits at the bottom of the form, above the action bar or the keyboard).
    // Measured before paint, so it never flashes in the wrong place.
    const listRef = useRef(null);
    const [listPlacement, setListPlacement] = useState({
        side: 'below',
        maxHeight: SUGGESTION_LIST_MAX_HEIGHT,
    });
    useLayoutEffect(() => {
        const list = listRef.current;
        if (!popoverOpen || !list)
            return;
        // The list's containing block: the input wrapper, or the whole chip box
        // on phones (see the list's classes).
        const anchor = list.offsetParent ?? list.parentElement;
        if (!anchor)
            return;
        const rect = anchor.getBoundingClientRect();
        const bounds = visibleBounds(anchor);
        setListPlacement(pickSuggestionPlacement({
            spaceAbove: rect.top - bounds.top,
            spaceBelow: bounds.bottom - rect.bottom,
            listHeight: list.scrollHeight,
        }));
    }, [popoverOpen, suggestions.length, showCreateRow]);
    const commitOption = (option) => {
        if (!linkedLabels.has(option.label.toLowerCase()))
            onAddOption(option);
        setInputValue('');
    };
    const commitFreeText = (label) => {
        const trimmed = label.trim();
        if (trimmed && !linkedLabels.has(trimmed.toLowerCase()))
            onAddFreeText(trimmed);
        setInputValue('');
    };
    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const exact = suggestions.find((o) => o.label.toLowerCase() === trimmedInput.toLowerCase());
            if (exact) {
                commitOption(exact);
            }
            else if (trimmedInput) {
                commitFreeText(trimmedInput);
            }
        }
        else if (e.key === 'Escape') {
            setInputValue('');
        }
        else if (e.key === 'Backspace' && inputValue === '' && removable.length > 0) {
            onRemove(removable[removable.length - 1].key);
        }
    };
    return (_jsxs("div", { className: cn('border-border/40 relative flex flex-wrap items-center gap-1.5 rounded border px-2 py-1.5', readOnly ? 'bg-muted/10' : 'bg-muted/20 focus-within:border-primary/40'), children: [chips.map((chip) => {
                const editable = !readOnly && !chip.locked;
                const removeKey = readOnly ? undefined : chip.locked ? chip.removeKey : chip.key;
                return (_jsxs("span", { className: cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs', chip.qualifier === 'ruled_out'
                        ? 'border-border/40 text-muted-foreground/70'
                        : chip.qualifier === 'suspected'
                            ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                            : 'border-teal-500/40 bg-teal-500/10 text-teal-200', chip.locked && 'border-dashed opacity-80'), "data-testid": `diagnosis-chip-${chip.label.toLowerCase()}`, "data-locked": chip.locked ? 'true' : undefined, children: [_jsx("button", { type: "button", disabled: !editable || !onCycle, onClick: () => onCycle?.(chip.key), className: cn('max-w-52 truncate pointer-coarse:py-1', chip.qualifier === 'ruled_out' && 'line-through', editable && onCycle && 'cursor-pointer'), title: chip.locked
                                ? (chip.lockedTitle ?? chip.label)
                                : !editable || !onCycle
                                    ? chip.label
                                    : t('diagnoses.cycleHint', {
                                        defaultValue: 'Click to cycle: present / suspected / ruled out',
                                    }), "aria-label": t('diagnoses.chipAria', {
                                label: chip.label,
                                qualifier: chip.qualifier.replace('_', ' '),
                                defaultValue: '{{label}}, {{qualifier}}',
                            }), children: chip.qualifier === 'suspected' ? `${chip.label}?` : chip.label }), removeKey !== undefined && (_jsx("button", { type: "button", onClick: () => onRemove(removeKey), 
                            // A 16px box around the 12px X plus a transparent 4px ::after
                            // ring: 24px to hit without growing the chip, and the ring
                            // stops at the 4px gap so it never takes a tap meant for the
                            // label. Touch-first: a 28px box (36px with the ring), its
                            // negative margins keeping the chip's height.
                            className: "text-muted-foreground hover:text-foreground relative -mr-1 inline-flex size-4 items-center justify-center rounded-full after:absolute after:-inset-1 after:content-[''] pointer-coarse:-my-1.5 pointer-coarse:size-7", "aria-label": t('diagnoses.removeAria', {
                                label: chip.label,
                                defaultValue: 'Remove {{label}}',
                            }), children: _jsx(X, { className: "h-3 w-3", "aria-hidden": "true" }) }))] }, chip.key));
            }), !readOnly && (
            // Positioned from sm up only: on phones the suggestion list anchors
            // to the whole chip box and spans its width, so it never runs off
            // the right edge from an input that wrapped behind a row of chips.
            _jsxs("div", { className: "min-w-40 flex-1 sm:relative", children: [_jsx("input", { type: "text", 
                        // Tag names are clinical terms: autocorrect and auto-capitals
                        // rewrite them on iPhone/iPad keyboards.
                        autoCapitalize: "none", autoCorrect: "off", spellCheck: false, value: inputValue, onChange: (e) => setInputValue(e.target.value), onFocus: () => setInputFocused(true), onBlur: () => {
                            // Delay so a mousedown on a suggestion row lands before close.
                            setTimeout(() => setInputFocused(false), 150);
                        }, onKeyDown: handleInputKeyDown, placeholder: chips.length === 0
                            ? t('diagnoses.placeholder', {
                                defaultValue: 'Add a clinical tag (e.g. pneumonia)...',
                            })
                            : t('diagnoses.placeholderMore', { defaultValue: 'Add...' }), className: "placeholder:text-muted-foreground/40 w-full bg-transparent py-0.5 text-xs focus:outline-none pointer-coarse:min-h-8 pointer-coarse:py-1.5", "aria-label": t('diagnoses.inputAria', { defaultValue: 'Add a clinical tag' }), "data-testid": `${testIdPrefix}-input` }), popoverOpen && (_jsxs("ul", { ref: listRef, className: cn('border-border absolute z-20 overflow-y-auto rounded-md border bg-[#1F2127] py-1 shadow-lg max-sm:inset-x-0 sm:left-0 sm:w-72', listPlacement.side === 'above' ? 'bottom-full mb-1' : 'top-full mt-1'), style: { maxHeight: listPlacement.maxHeight }, role: "listbox", "data-testid": `${testIdPrefix}-suggestions`, "data-placement": listPlacement.side, children: [suggestions.map((option) => (_jsx("li", { children: _jsxs("button", { type: "button", role: "option", "aria-selected": false, 
                                    // mousedown, not click: the input's blur fires first on
                                    // click and would unmount the row mid-press.
                                    onMouseDown: (e) => {
                                        e.preventDefault();
                                        commitOption(option);
                                    }, className: "hover:bg-accent flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs pointer-coarse:py-2.5", children: [_jsx("span", { className: "truncate", children: option.label }), _jsx("span", { className: "text-muted-foreground/60 shrink-0 text-[10px]", children: option.source === 'vocabulary'
                                                ? t('diagnoses.suggested', { defaultValue: 'suggested' })
                                                : (option.category ?? '') })] }) }, `${option.source}:${option.label.toLowerCase()}`))), showCreateRow && (_jsx("li", { children: _jsxs("button", { type: "button", role: "option", "aria-selected": false, onMouseDown: (e) => {
                                        e.preventDefault();
                                        commitFreeText(trimmedInput);
                                    }, className: "hover:bg-accent text-primary flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-xs pointer-coarse:py-2.5", "data-testid": `${testIdPrefix}-create-option`, children: [_jsx(Plus, { className: "h-3 w-3", "aria-hidden": "true" }), t('diagnoses.createOption', {
                                            label: trimmedInput,
                                            defaultValue: 'Create "{{label}}"',
                                        })] }) })), suggestions.length === 0 && !showCreateRow && (_jsx("li", { className: "text-muted-foreground px-3 py-1.5 text-xs", children: t('diagnoses.noMatches', { defaultValue: 'No matches' }) }))] }))] }))] }));
}
